import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SendHorizonal } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface TextPromptInputProps {
  onSubmit: (text: string) => void;
  isPending: boolean;
}

export function TextPromptInput({ onSubmit, isPending }: Readonly<TextPromptInputProps>) {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  const trimmed = text.trim();
  const isDisabled = isPending || trimmed.length === 0;

  const handleSubmit = () => {
    if (!isDisabled) {
      onSubmit(trimmed);
      setText('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <textarea
        aria-label={t('textPrompt.placeholder')}
        placeholder={t('textPrompt.placeholder')}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isPending}
        rows={3}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none"
      />
      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={isDisabled} aria-label={t('textPrompt.submit')}>
          {isPending ? (
            <>
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
              {t('textPrompt.processing')}
            </>
          ) : (
            <>
              <SendHorizonal className="h-4 w-4" />
              {t('textPrompt.submit')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
